import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Users, DollarSign, Activity, FileText, MessageSquare, Power, PowerOff, Crown } from "lucide-react";
import Layout from "../components/Layout";
import api, { errMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TIERS = ["none", "starter", "growth", "pro"];

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [smsLog, setSmsLog] = useState([]);
  const [tab, setTab] = useState("users");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, a, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/audit-log"),
        api.get("/admin/sms-log"),
      ]);
      setStats(s.data);
      setUsers(u.data || []);
      setAudit(a.data || []);
      setSmsLog(l.data || []);
    } catch (e) {
      toast.error(errMessage(e, "Could not load admin data"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user, load]);

  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  const updateUser = async (id, patch) => {
    try {
      await api.patch(`/admin/users/${id}`, patch);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(errMessage(e, "Update failed"));
    }
  };

  const filtered = users.filter((u) =>
    !search.trim()
      ? true
      : (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
        (u.full_name || "").toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Layout>
      <div className="px-8 py-10 max-w-7xl">
        <div className="flex items-center gap-3 mb-2">
          <Shield size={20} strokeWidth={1.5} />
          <div className="overline">// Admin console</div>
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mb-6">Admin.</h1>

        {loading || !stats ? (
          <div className="text-sm text-[--ink-muted] py-12 text-center">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
              <Stat icon={Users} label="Total users" value={stats.total_users} />
              <Stat icon={Crown} label="Paid / trialing" value={stats.paid_or_trialing} />
              <Stat icon={DollarSign} label="Est. MRR" value={`$${Number(stats.estimated_mrr).toLocaleString()}`} />
              <Stat icon={Activity} label="Signups 30d" value={stats.signups_30d} />
            </div>

            <div className="surface-card p-5 mb-8">
              <div className="overline mb-3">// Tier distribution</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm font-mono">
                {TIERS.map((t) => (
                  <div key={t} className="flex items-center justify-between border border-[--line] px-3 py-2">
                    <span className="text-[--ink-muted] uppercase tracking-wider text-xs">{t}</span>
                    <span className="font-bold">{stats.by_tier[t] || 0}</span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-[--ink-muted] mt-3 font-mono">
                {stats.admins} admin · {stats.disabled} disabled
              </div>
            </div>

            <div className="flex items-center gap-1 mb-4 border-b border-[--line]">
              {[
                { id: "users", label: `Users (${users.length})` },
                { id: "audit", label: `Audit log (${audit.length})` },
                { id: "sms", label: `SMS log (${smsLog.length})` },
              ].map((x) => (
                <button
                  key={x.id}
                  data-testid={`admin-tab-${x.id}`}
                  onClick={() => setTab(x.id)}
                  className={`px-4 py-2 text-sm font-medium ${
                    tab === x.id
                      ? "border-b-2 border-[--ink] text-[--ink]"
                      : "text-[--ink-muted] hover:text-[--ink]"
                  }`}
                >
                  {x.label}
                </button>
              ))}
            </div>

            {tab === "users" && (
              <>
                <input
                  data-testid="admin-user-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by email or name…"
                  className="w-full md:w-80 mb-4 text-sm"
                />
                <div className="surface-card overflow-x-auto" data-testid="admin-users-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[--line] text-xs text-[--ink-muted] uppercase tracking-wider">
                        <Th>Email</Th>
                        <Th>Tier</Th>
                        <Th>Status</Th>
                        <Th>Activity</Th>
                        <Th>Roles</Th>
                        <Th>Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((u) => (
                        <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-[--line] hover:bg-[--bg-soft]">
                          <td className="py-3 px-4">
                            <div className="font-semibold text-[13px]">{u.full_name || "—"}</div>
                            <div className="font-mono text-xs text-[--ink-muted]">{u.email}</div>
                          </td>
                          <td className="py-3 px-4">
                            <select
                              data-testid={`admin-tier-${u.id}`}
                              value={u.subscription_tier || "none"}
                              onChange={(e) => updateUser(u.id, { subscription_tier: e.target.value })}
                              className="text-xs font-mono"
                            >
                              {TIERS.map((t) => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                            {u.is_grandfathered && (
                              <div className="text-[9px] text-[--accent] mt-1 font-mono uppercase tracking-wider">Grandfathered</div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-xs font-mono">
                            <div>{u.subscription_status || "—"}</div>
                            {u.disabled && (
                              <div className="text-red-600">DISABLED</div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-xs font-mono text-[--ink-muted]">
                            {u.counts?.saved_searches || 0}m / {u.counts?.saved_deals || 0}d /
                            {" "}{u.counts?.buyers || 0}b / {u.counts?.outreach || 0}o
                          </td>
                          <td className="py-3 px-4 text-xs">
                            {u.is_admin && (
                              <span className="inline-block bg-[--ink] text-white px-1.5 py-0.5 font-mono uppercase tracking-wider text-[9px]">Admin</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1.5">
                              <button
                                data-testid={`admin-toggle-disabled-${u.id}`}
                                onClick={() => updateUser(u.id, { disabled: !u.disabled })}
                                disabled={u.id === user.id}
                                className="btn-ghost !py-1 !px-2 text-xs disabled:opacity-30"
                                title={u.disabled ? "Re-enable" : "Disable"}
                              >
                                {u.disabled ? <Power size={11} strokeWidth={1.5} /> : <PowerOff size={11} strokeWidth={1.5} />}
                              </button>
                              <button
                                data-testid={`admin-toggle-admin-${u.id}`}
                                onClick={() => updateUser(u.id, { is_admin: !u.is_admin })}
                                disabled={u.id === user.id}
                                className="btn-ghost !py-1 !px-2 text-xs disabled:opacity-30"
                                title={u.is_admin ? "Remove admin" : "Make admin"}
                              >
                                <Crown size={11} strokeWidth={1.5} />
                              </button>
                              <button
                                data-testid={`admin-toggle-gf-${u.id}`}
                                onClick={() => updateUser(u.id, { is_grandfathered: !u.is_grandfathered })}
                                className="btn-ghost !py-1 !px-2 text-xs"
                                title={u.is_grandfathered ? "Remove grandfather" : "Grandfather"}
                              >
                                ★
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs text-[--ink-muted] mt-3 font-mono">
                  Activity legend · m=markets · d=deals · b=buyers · o=outreach
                </div>
              </>
            )}

            {tab === "audit" && (
              <div className="surface-card p-5" data-testid="admin-audit-log">
                <div className="overline mb-3 flex items-center gap-1.5">
                  <FileText size={11} strokeWidth={1.5} /> Audit log
                </div>
                {audit.length === 0 ? (
                  <div className="text-xs text-[--ink-muted] py-6 text-center">No actions yet</div>
                ) : (
                  <div className="space-y-2">
                    {audit.map((a) => (
                      <div key={a.id} className="text-xs font-mono border-b border-[--line] pb-2">
                        <div>
                          <span className="text-[--ink-muted]">{new Date(a.at).toLocaleString()}</span>{" "}
                          <span className="font-semibold">{a.actor_email}</span>{" "}
                          <span className="text-[--accent]">{a.action}</span>{" "}
                          → <span>{a.target?.email}</span>
                        </div>
                        {Object.keys(a.meta || {}).length > 0 && (
                          <div className="text-[--ink-muted] mt-0.5 ml-3">
                            {JSON.stringify(a.meta)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "sms" && (
              <div className="surface-card p-5" data-testid="admin-sms-log">
                <div className="overline mb-3 flex items-center gap-1.5">
                  <MessageSquare size={11} strokeWidth={1.5} /> Recent SMS / voice outreach
                </div>
                {smsLog.length === 0 ? (
                  <div className="text-xs text-[--ink-muted] py-6 text-center">No outreach logged</div>
                ) : (
                  <div className="space-y-2">
                    {smsLog.map((m) => (
                      <div key={m.id} className="text-xs font-mono border-b border-[--line] pb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[--ink-muted]">{new Date(m.created_at).toLocaleString()}</span>
                          <span className="bg-[--bg-soft] px-1.5 py-0.5 text-[10px] uppercase tracking-wider">{m.channel}</span>
                          <span className={m.sent ? "text-green-700" : "text-red-700"}>
                            {m.sent ? "sent" : (m.provider_status || "failed")}
                          </span>
                          <span className="text-[--ink-muted]">·</span>
                          <span>{m.user_email}</span>
                          <span className="text-[--ink-muted]">→</span>
                          <span>{m.to}</span>
                        </div>
                        <div className="text-[--ink-muted] mt-0.5 ml-2 truncate">
                          {m.property_address && `${m.property_address} · `}
                          {m.body?.slice(0, 120)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="surface-card p-4">
      <div className="flex items-center gap-2 text-[--ink-muted] mb-2">
        <Icon size={12} strokeWidth={1.5} />
        <span className="text-[10px] uppercase tracking-wider font-mono">{label}</span>
      </div>
      <div className="font-display text-2xl font-bold tracking-tighter">{value}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left py-3 px-4 font-medium">{children}</th>;
}
